import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { checkIsAdmin } from "@/lib/blogAdmin.functions";

export const Route = createFileRoute("/tools")({
  head: () => ({ meta: [{ title: "Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const [state, setState] = useState<"loading" | "ok" | "no-auth" | "no-admin">("loading");
  const isLoginPage =
    typeof window !== "undefined" && window.location.pathname === "/tools/login";

  useEffect(() => {
    if (isLoginPage) return;
    let cancelled = false;
    async function check() {
      try {
        const res = await checkIsAdmin();
        if (cancelled) return;
        setState(res.isAdmin ? "ok" : "no-admin");
      } catch {
        if (!cancelled) setState("no-auth");
      }
    }
    check();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "TOKEN_REFRESHED") {
        check();
      }
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [isLoginPage]);

  useEffect(() => {
    if (state === "no-auth" && window.location.pathname !== "/tools/login") {
      navigate({ to: "/tools/login" });
    }
  }, [state, navigate]);

  if (isLoginPage) return <Outlet />;
  if (state === "loading") {
    return <div className="container mx-auto px-6 py-24 text-muted-foreground">Loading…</div>;
  }
  if (state === "no-admin") {
    return (
      <section className="container mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Not authorized</h1>
        <p className="mt-3 text-muted-foreground">
          Your account is signed in but doesn't have admin access. Ask an existing admin to grant you
          the <code>admin</code> role in the user_roles table.
        </p>
        <Button
          className="mt-6"
          variant="outline"
          onClick={async () => {
            await supabase.auth.signOut();
            navigate({ to: "/tools/login" });
          }}
        >
          Sign out
        </Button>
      </section>
    );
  }
  if (state === "no-auth") return null;

  return (
    <div className="container mx-auto px-6 py-10">
      <header className="mb-8 flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-6">
          <Link to="/tools" className="font-display text-xl font-bold">
            Admin
          </Link>
          <Link
            to="/tools"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Tools
          </Link>
          <Link
            to="/tools/blog"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Blog posts
          </Link>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            await supabase.auth.signOut();
            navigate({ to: "/tools/login" });
          }}
        >
          Sign out
        </Button>
      </header>
      <Outlet />
    </div>
  );
}
